import React, { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { tasksSelector } from 'entities/task/model/slice/taskSlice';
import { Task } from 'entities/task/model/types/tasks';
import { Icon, MultiSelect } from 'shared/ui';

import cls from './MultiSelectTags.module.css';
import { FieldErrors } from '../../entities/modal/model/types/modalFieldErrors';
import { TModalMode } from '../../shared/model/types/modalMode.type';
import { useAppDispatch, useAppSelector } from '../../store';
import { changeModalTags } from '../../widgets/Modals/model/slice/addModalSlice';
import { changeEditModalTags } from '../../widgets/Modals/model/slice/editModalSlice';

interface IMultiSelectTags {
    modalMode: TModalMode;
    isDisabled: boolean;
    error: FieldErrors['tags'];
    setFieldErrors: React.Dispatch<React.SetStateAction<FieldErrors>>;
}

export const MultiSelectTags = ({
    modalMode,
    isDisabled,
    error,
    setFieldErrors,
}: IMultiSelectTags) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { tags } = useAppSelector((state) => state.tasksInformationReducer);
    const [tagsList, setTagsList] = useState([]);
    const pickedTaskUid = new URLSearchParams(window.location.search).get('task_id');
    const taskData: Task = useAppSelector((state) =>
        tasksSelector.selectById(state, pickedTaskUid)
    );

    const [selectedTags, setSelectedTags] = useState(() => {
        if (modalMode === 'addNewTask') return [];
        if (!taskData?.involved) return tags.map((tag) => ({ title: tag.title, value: tag.id }));
        return tags
            .filter((tagId) => taskData.context.tags.includes(tagId.id))
            .map((tag) => ({ title: tag.title, value: tag.id }));
    });

    useEffect(() => {
        if (tags) {
            const getTags = tags.map((tag) => ({ title: tag.title, value: tag.id }));
            setTagsList(getTags);
        }
    }, []);

    useEffect(() => {
        const formatSelectedTags = selectedTags.map((tag) => tag.title);
        if (modalMode === 'addNewTask') {
            dispatch(changeModalTags(formatSelectedTags));
        }
        if (modalMode === 'editTask') {
            dispatch(changeEditModalTags(formatSelectedTags));
        }
    }, [selectedTags]);

    const changeSelectedTags = useCallback(
        (value: string) => {
            const currentTag = tagsList.find((tag) => tag.value.toString() === value);
            // console.log('currentTag: ', currentTag);
            setSelectedTags((prevState) => [...prevState, currentTag]);
        },
        [tagsList]
    );
    const addNewTag = useCallback(
        (newTag: { value: number; title: string }) => {
            // console.log('newTag: ', newTag);
            setSelectedTags((prevState) => [newTag, ...prevState]);
            setTagsList((prevState) => [newTag, ...prevState]);

            setFieldErrors((prev) => ({
                ...prev,
                tags: { isError: false, errorMessage: null },
            }));
        },
        [tagsList]
    );

    const deleteTag = useCallback(
        (value: string) => {
            setSelectedTags((prevSelectedTags) =>
                prevSelectedTags.filter((tag) => tag.value.toString() !== value)
            );
            setFieldErrors((prev) => ({
                ...prev,
                tags: { isError: false, errorMessage: null },
            }));
        },
        [setSelectedTags]
    );

    return (
        <MultiSelect
            isSmartPosition
            selected={selectedTags}
            placeholder={t('Features.MultiSelectTags.placeholder.text')}
            onChange={changeSelectedTags}
            onAddNewTag={addNewTag}
            deleteHandle={deleteTag}
            options={tagsList}
            showCheckboxes
            isDisabled={isDisabled}
            leftIcon={
                <span className={cls.IconWrapper}>
                    <Icon iconName="taskTags" />
                </span>
            }
            error={error.errorMessage}
        />
    );
};
